import { Col, SelectField } from '@/components';
import type { AnalysisSearchFormType } from '@/features/search/_models/analysis-search';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import {
  AggregationTypeLabelModifier,
  aggregationTypeEnum,
} from '@/utils/enum';
import { type FC, memo, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// 階層表示のためのオプション構造を作成
const createHierarchicalOptions = (isPaymentTarget: boolean) => {
  // 分析対象が「支出」の場合は「支出額」のみ表示
  if (isPaymentTarget) {
    return [
      {
        value: aggregationTypeEnum.enum.payment,
        label: AggregationTypeLabelModifier[aggregationTypeEnum.enum.payment],
      },
    ];
  }

  return [
    // 既存の基本選択肢
    {
      value: aggregationTypeEnum.enum.finalized,
      label: AggregationTypeLabelModifier[aggregationTypeEnum.enum.finalized],
    },
    // 予算種別グループ
    {
      value: aggregationTypeEnum.enum.finalizedForInitialBudget,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForInitialBudget
        ],
    },
    // 補正予算グループ
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget
        ],
    },
    // 第X次補正予算
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget_1,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget_1
        ],
    },
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget_2,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget_2
        ],
    },
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget_3,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget_3
        ],
    },
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget_4,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget_4
        ],
    },
    {
      value: aggregationTypeEnum.enum.finalizedForSupplementaryBudget_5,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForSupplementaryBudget_5
        ],
    },
    // その他の予算種別
    {
      value: aggregationTypeEnum.enum.finalizedForReserveBudget,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForReserveBudget
        ],
    },
    {
      value: aggregationTypeEnum.enum.finalizedForCarriedForwardBudget,
      label:
        AggregationTypeLabelModifier[
          aggregationTypeEnum.enum.finalizedForCarriedForwardBudget
        ],
    },
  ];
};

export const AggregationTypeColField: FC = memo(() => {
  const { t } = useTranslation();

  const {
    control,
    formState: { errors },
  } = useFormContext<AnalysisSearchFormType>();

  const analysisTarget = useWatch({ control, name: 'analysisTarget' });
  const isPaymentTarget = analysisTarget === 'payment';

  const options = useMemo(
    () => createHierarchicalOptions(isPaymentTarget),
    [isPaymentTarget],
  );

  return (
    <Col span={4} style={{ minWidth: COL_MIN_WIDTH.basic }}>
      <SelectField
        name="aggregationType"
        label={t('aggregation-type')}
        control={control}
        options={options}
        allowClear={false}
        errors={errors}
        disabled={isPaymentTarget}
      />
    </Col>
  );
});
